'use client'
import { useMemo, useState } from 'react'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useLanguage } from '@/lib/context/LanguageContext'
import { buildContactSchema, type ContactFormData } from '@/lib/schemas/contact'

type FieldErrors = Partial<Record<keyof ContactFormData | 'attachment', string>>

export default function ContactForm() {
  const { t, lang } = useLanguage()
  const schema = useMemo(() => buildContactSchema(lang), [lang])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setError,
    setValue,
    watch,
  } = useForm<ContactFormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  })

  const [serverError, setServerError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const attachment: File | undefined = watch('attachment') as File | undefined

  const onSubmit: SubmitHandler<ContactFormData> = async (data) => {
    setServerError(null)
    const fd = new FormData()
    fd.append('name', data.name)
    fd.append('company', data.company)
    fd.append('email', data.email)
    if (data.phone) fd.append('phone', data.phone)
    if (data.country) fd.append('country', data.country)
    if (data.message) fd.append('message', data.message)
    fd.append('locale', lang)
    // Always forward the honeypot value (even when empty — backend distinguishes).
    fd.append('website', data.website ?? '')
    if (data.attachment instanceof File) fd.append('attachment', data.attachment)

    try {
      const res = await fetch('/api/contact', { method: 'POST', body: fd })
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        fieldErrors?: FieldErrors
        error?: string
      }

      if (!res.ok) {
        if (json.fieldErrors) {
          for (const [k, v] of Object.entries(json.fieldErrors)) {
            if (v) setError(k as keyof ContactFormData, { type: 'server', message: v })
          }
        }
        setServerError(json.error ?? t.contact.error)
        return
      }

      setSubmitted(true)
      reset()
    } catch {
      setServerError(t.contact.error)
    }
  }

  if (submitted) {
    return (
      <div role="status" className="rounded-[var(--radius-card)] border border-accent/30 bg-accent-soft/40 p-8 sm:p-10">
        <p className="text-[11px] uppercase tracking-[0.22em] text-accent font-medium">
          {t.contact.section}
        </p>
        <h3
          className="mt-3 font-display-serif text-2xl text-ink"
          style={{ fontVariationSettings: '"opsz" 32, "wght" 620' }}
        >
          {t.contact.successTitle}
        </h3>
        <p className="mt-3 text-ink-muted">{t.contact.successSub}</p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-[var(--radius-card)] border border-line bg-surface p-7 sm:p-9 shadow-[0_30px_60px_-30px_rgba(26,26,23,0.18)]"
      noValidate
    >
      {/* Honeypot. Hidden off-screen, no autofocus, no autocomplete, no
          tabIndex. Real users never see/fill it; bots that scrape DOM do.
          A non-empty submission is silently dropped server-side. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
        <label>
          Website (do not fill)
          <input
            type="text"
            autoComplete="off"
            tabIndex={-1}
            {...register('website')}
          />
        </label>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field fieldId="cf-name" label={t.contact.name} error={errors.name?.message} required>
          <input
            id="cf-name"
            type="text"
            autoComplete="name"
            aria-required="true"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'cf-name-error' : undefined}
            {...register('name')}
            className={inputCls}
          />
        </Field>
        <Field fieldId="cf-company" label={t.contact.company} error={errors.company?.message} required>
          <input
            id="cf-company"
            type="text"
            autoComplete="organization"
            aria-required="true"
            aria-invalid={!!errors.company}
            aria-describedby={errors.company ? 'cf-company-error' : undefined}
            {...register('company')}
            className={inputCls}
          />
        </Field>
        <Field fieldId="cf-email" label={t.contact.email} error={errors.email?.message} required>
          <input
            id="cf-email"
            type="email"
            autoComplete="email"
            dir="ltr"
            aria-required="true"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'cf-email-error' : undefined}
            {...register('email')}
            className={inputCls}
          />
        </Field>
        <Field fieldId="cf-phone" label={t.contact.phone} error={errors.phone?.message}>
          <input
            id="cf-phone"
            type="tel"
            autoComplete="tel"
            dir="ltr"
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'cf-phone-error' : undefined}
            {...register('phone')}
            className={inputCls}
          />
        </Field>
        <Field fieldId="cf-country" label={t.contact.country} error={errors.country?.message} className="sm:col-span-2">
          <input
            id="cf-country"
            type="text"
            autoComplete="country-name"
            aria-invalid={!!errors.country}
            aria-describedby={errors.country ? 'cf-country-error' : undefined}
            {...register('country')}
            className={inputCls}
          />
        </Field>
        <Field
          fieldId="cf-message"
          label={t.contact.message}
          error={errors.message?.message}
          className="sm:col-span-2"
        >
          <textarea
            id="cf-message"
            rows={4}
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? 'cf-message-error' : undefined}
            {...register('message')}
            className={`${inputCls} resize-y`}
          />
        </Field>

        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.18em] text-ink-muted">
            {t.contact.attachmentLabel}
          </label>
          <div className="mt-3 flex items-center gap-3">
            <input
              id="contact-attachment"
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              className="sr-only"
              onChange={(e) => setValue('attachment', e.target.files?.[0])}
            />
            <label
              htmlFor="contact-attachment"
              className="cursor-pointer rounded-none border border-ink/15 px-4 py-2 text-xs font-medium text-ink hover:border-ink/30 hover:bg-ink/[0.03] transition-colors"
            >
              {attachment ? t.contact.attachmentChange : '+ ' + t.contact.attachmentLabel.split('(')[0].trim()}
            </label>
            {attachment && (
              <>
                <span className="text-xs text-ink-muted" style={{ direction: 'ltr' }}>
                  {attachment.name} · {(attachment.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  onClick={() => setValue('attachment', undefined)}
                  className="text-xs text-accent hover:underline"
                >
                  {t.contact.attachmentRemove}
                </button>
              </>
            )}
          </div>
          {errors.attachment?.message && (
            <p role="alert" className="mt-2 text-xs text-accent">{String(errors.attachment.message)}</p>
          )}
        </div>
      </div>

      {serverError && (
        <p
          role="alert"
          className="mt-5 rounded-md border border-accent/30 bg-accent-soft/40 px-4 py-3 text-sm text-accent-deep"
        >
          {serverError}
        </p>
      )}

      <div className="mt-7 flex items-center justify-between gap-4">
        <p className="text-xs text-ink-muted leading-relaxed max-w-sm">
          {t.footer.disclaimer}
        </p>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 rounded-none bg-ink text-surface px-6 py-3 text-sm font-semibold disabled:opacity-60 hover:bg-accent transition-colors"
        >
          {isSubmitting ? t.contact.submitting : t.contact.submit}
        </button>
      </div>
    </form>
  )
}

const inputCls =
  'w-full rounded-md border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-ink-muted/60 focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/25 transition'

function Field({
  fieldId,
  label,
  error,
  required = false,
  children,
  className,
}: {
  fieldId: string
  label: string
  error?: string
  required?: boolean
  children: React.ReactNode
  className?: string
}) {
  return (
    <label htmlFor={fieldId} className={`flex flex-col ${className ?? ''}`}>
      <span className="mb-2 text-xs uppercase tracking-[0.18em] text-ink-muted">
        {label}
        {required && (
          <>
            <span aria-hidden className="text-action"> *</span>
            <span className="sr-only"> (required)</span>
          </>
        )}
      </span>
      {children}
      {error && (
        <span id={`${fieldId}-error`} role="alert" className="mt-2 text-xs text-accent">
          {error}
        </span>
      )}
    </label>
  )
}
